import { useTranslation } from "react-i18next";
import { IntegrationAccess, IntegrationThirdParty, IntegrationType } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/companyIntegrationLimitation";
import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { CREATE_THIRD_PARTY_INTEGRATIONS, CreateThirdPartyIntegrationsArgs, CreateThirdPartyIntegrationsResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/createIntegrationThirdParty";
import { CardsView, LoadingSpinner, MainContentPaddingTypes, NewButton, NewInput, NewModal, PageHeader, TooltipString } from "../../../../Components";
import { colors, css, fonts, frames, glyphs } from "../../../../Styles";
import { ButtonTypes, LocaleFarmDate } from "../../../../Definitions";
import { Graph_QL, useUser } from "../../../../Services";
import { useAppSelector } from "../../../../Store";
import { MAINTAIN_COMPANY_INTEGRATION_LIMITATIONS, MaintainCompanyIntegrationLimitationsArgs, MaintainCompanyIntegrationLimitationsResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/maintainCompanyIntegrationLimitations";
import { COMPANY_INTEGRATIONS, CompanyIntegrationsResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/companyIntegrations";
import { ExtendedCompaniesArgs } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/extendedCompanies";
import { INTEGRATION_TYPES, IntegrationTypesResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/integrationTypes";
import { t } from "i18next";

export default function Integrations() { 
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({ domain: farm })), [farm]);
  const { userFarms } = useUser();
    
  const companyId = userFarms && userFarms.find(e => e.domain === domain)?.company_id

  const [fetchCompany, { data: companyFetchData, loading: companyFetchLoading, error: companyFetchError }] = useLazyQuery<CompanyIntegrationsResponse, ExtendedCompaniesArgs>(COMPANY_INTEGRATIONS, {
    fetchPolicy: "no-cache",
    context: {
      headers: {
        tocloud: true,
      },
    },
  });

  const { loading: integrationTypesLoading } = useQuery<IntegrationTypesResponse, ExtendedCompaniesArgs>(INTEGRATION_TYPES, {
    fetchPolicy: "no-cache",
    context: {
      headers: {
        tocloud: true
      }
    }
  });

  const [maintainLimitations, { loading: maintainLimitationsLoading, reset: maintainLimitationsReset }] =
    useMutation<MaintainCompanyIntegrationLimitationsResponse, MaintainCompanyIntegrationLimitationsArgs>(MAINTAIN_COMPANY_INTEGRATION_LIMITATIONS, {
      fetchPolicy: "no-cache",
      context: {
        headers: {
          tocloud: true
        }
      }
    });
  
  const companyData = companyFetchData?.user.extendedCompanies?.[0];

  const sortedCompanyData = companyData?.integrationLimitations?.sort((a, b) => {
    const aExists = companyData.integrations.find((e) => e.integrationType.integration_type_id === a.integration_type_id);
    const bExists = companyData.integrations.find((e) => e.integrationType.integration_type_id === b.integration_type_id);

    return Number(!!aExists) - Number(!!bExists);
  })

  useEffect(() => {
    fetchCompany({
      variables: { input: { ids: [companyId ?? 0] } }
    });
    maintainLimitationsReset();
  }, [companyId, fetchCompany, maintainLimitationsReset]);

  const dataLoading = companyFetchLoading || integrationTypesLoading || maintainLimitationsLoading;

  return (
    <frames.Page_inner>
      <PageHeader topHeader='Companies' subHeader='Integrations' />
      <frames._page_inner_content $hasScroll>
        <frames._page_inner_content_scroll>
          {companyFetchError ? (
            <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed }}> Error loading integrations </p>
          ) : ( <>
            { dataLoading && <LoadingSpinner />}
            { !dataLoading &&  
              <div style={{}}>
                <CardsView
                  addExtraHeight
                  items={sortedCompanyData?.map((integrationLimitation, index) => (
                    <IntegrationCard
                      key={index}
                      domain={domain}
                      companyId={companyId}
                      existingIntegration={companyData?.integrations.find((e) => e.integrationType.integration_type_id === integrationLimitation.integration_type_id)}
                      integrationLimitation={integrationLimitation}
                    />
                  ))}
                />
              </div>   
            }
          </>)}
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>
    </frames.Page_inner>
  )
}

// #region | Integration Components
function IntegrationCard({ domain, existingIntegration, companyId, integrationLimitation }: { domain: string, existingIntegration: IntegrationThirdParty | undefined, companyId: number | undefined, integrationLimitation: any }) {
  const [copied, setCopied] = useState(false);
  const [typeToCreate, setTypeToCreate] = useState<IntegrationType>();

  const expired = !!existingIntegration?.integration_expiration && new Date(existingIntegration.integration_expiration * 1000) < new Date();
  const statusColor = !existingIntegration ? colors.greyDarkSecondary : expired ? colors.systemRed : colors.blueLightPrimary;
  const noPermissions = !existingIntegration?.integrationAccess?.temp && !existingIntegration?.integrationAccess?.water && !existingIntegration?.integrationAccess?.weight;
  
  const rows = [
    { label: "Name", value: existingIntegration?.description ?? "-" },
    { label: "Expiration", value: existingIntegration?.integration_expiration ? new LocaleFarmDate( existingIntegration.integration_expiration * 1000 ).toLocaleFarmString() : "No expiration" },
    { label: "Token", value: existingIntegration?.integration_jwt },
    { label: "Permissions", value: noPermissions ? <p style={{ ...css.emptyPen, opacity: "0.5" }}>No permissions</p> : <div style={{ display: "flex", flexDirection: "column" }}>
      {[
        { name: "Temperature", isEnabled: existingIntegration?.integrationAccess?.temp },
        { name: "Water", isEnabled: existingIntegration?.integrationAccess?.water },
        { name: "Weight", isEnabled: existingIntegration?.integrationAccess?.weight }
      ].map(e =>
        <p key={e.name} style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
          {e.isEnabled && e.name}
        </p>
      )}
    </div> }
  ];

  return ( <>
    <div key={integrationLimitation.integration_type_id} style={{
      breakInside: "avoid",
      border: "5px solid " + colors.greyPrimary, 
      backgroundColor: existingIntegration ? colors.greyLightTertiary : colors.greyPrimary,
      minWidth: "310px",
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "10px",
    }}>

      {/* Header */}
      <div style={{backgroundColor: colors.greyPrimary, height: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative" }}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary }}>{integrationLimitation.integration_name ?? "-"}</p>
        <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>Integration</p>
        <div style={{position: "absolute", top: "0px", right: "0px"}}>
          <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
            {existingIntegration ? <>
              <frames.Active $color={statusColor}>•</frames.Active>
              <p style={{ ...fonts.header, ...fonts.fontSize5, color: statusColor, textAlign: "right" }}>{t("feed.feedCurve.active", { defaultValue: "Active" })}</p>
            </> : ""}
            <TooltipString
              glyphString
              contentString={<glyphs.Info/>}
              tooltipString={integrationLimitation.description ?? "No description"}
              overrides={{ tooltipString: { maxWidth: "200px", textAlign: "center" } }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: existingIntegration ? "20px 10px" : "0px 20px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px"}}>
        {existingIntegration ? (<>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {rows.map((row) => {
              if (existingIntegration.integrationType.access_level === 2 && row.label === "Permissions") return;

              return (
                <div style={{ display: "grid", gridTemplateColumns: "80px 1px minmax(0, 1fr)", gap: "10px", width: "100%" }}>
                  <div style={{display: "flex", flexDirection: "column", alignItems: "end", minHeight: "20px"}}>
                    <p style={{ ...fonts.paragraph, ...fonts.fontSize4, ...fonts.fontWeight2, color: colors.greyDarkSecondary }}>
                      {row.label}
                    </p>
                    {row.label === "Token" &&
                      <NewButton
                        content=""
                        buttonType={ButtonTypes.glyphButton}
                        disabled={copied}
                        startEnhancer={ copied ? glyphs.Check({ fillColor: colors.systemGreen, size: "15px" }) : glyphs.Edit({ fillColor: colors.greyPrimary, size: "15px" })}
                        onClick={async () => {
                          await navigator.clipboard.writeText( existingIntegration?.integration_jwt ?? "" );
                          setCopied(true);
                          setTimeout(() => { setCopied(false)  }, 2000);
                        }}
                        overrides={{
                          disabledStyles: {opacity: "1"}
                        }}
                      />
                    }
                  </div>
                  <div style={{borderLeft: "2px solid "+colors.greyTertiary}}/>
                  <div style={{}}>
                    <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, wordBreak: "break-word" }}>
                      {row.value}
                    </p>

                  </div>
                </div>
              )
            })}
          </div>            
        </> ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
            <div style={{ ...css.emptyPen, color: colors.greyLightTertiary, opacity: "0.5" }}>
              This integration has not been created yet!
            </div>
            { integrationLimitation.access_level === 2 && <NewButton
              content={"Create"}
              overrides={{
                baseStyles: { backgroundColor: colors.greySecondary },
                hoverStyles: { backgroundColor: colors.greyDarkSecondary }
              }}
              onClick={() => setTypeToCreate(integrationLimitation)}
            /> }
          </div>
        )} 
      </div>
    </div>
    
    {typeToCreate &&
      <CreateIntegrationModal
        closeModal={() => {setTypeToCreate(undefined)}}
        domain={domain}
        integrationType={typeToCreate}
      />
    }
  </> );
}
// #endregion

// #region | Create Integration Modal
interface CreateIntegrationModalProps {
	closeModal: () => void;
	domain: string;
	integrationType: IntegrationType;
}

function CreateIntegrationModal({ closeModal, domain, integrationType }: CreateIntegrationModalProps) {
	const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null)

	const [integrationState, setIntegrationState] = useState<{ description: string, access: Omit<IntegrationAccess, "integration_access_id"> }>({ description: "", access: {} });

	const [fetchData, { data, loading, error, reset }] = useMutation<CreateThirdPartyIntegrationsResponse, CreateThirdPartyIntegrationsArgs>(CREATE_THIRD_PARTY_INTEGRATIONS, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	async function handleCommit() {
		await fetchData({
			variables: {
				domain,
				inputs: [{
          description: integrationState.description,
          fk_integration_type: integrationType.integration_type_id,
        }]
			}
    });
    if (isError) {
      setMessage("failed to create integraton")
      setTimeout(() => setMessage(null), 2000);
    } else {
      closeModal();
    }
	}

	function handleCloseModal() {
    setIntegrationState({ description: "", access: {} });
		reset();
		closeModal();
	}

	const isError = error || (data?.company && !data.company.createThirdPartyIntegrations.success);

	return (
		<NewModal
			predefinedButtons={{
				onClose: handleCloseModal
			}}
			header={"Create Integration"}
			overrides={{
				mainContentPadding: MainContentPaddingTypes.secondary,
			}}
			mainContent={
				<div style={{ display: "flex", flexDirection: "column", gap: "5px", padding: "0px 20px" }}>
					<NewInput
						header="description"
						onChange={(e) => setIntegrationState(prev => ({ ...prev, description: e }))}
					/>
				</div>
			}
			footerContent={
				<div style={{ margin: "0px 20px" }}>
					{!(loading || isError || data?.company) &&
						<NewButton
							content={t("buttons.create", { defaultValue: "create" })}
							onClick={handleCommit}
							overrides={{
								baseStyles: {
									backgroundColor: colors.greyDarkPrimary,
									border: "3px solid " + colors.blueDarkPrimary
								},
							}}
						/>
					}
				</div>
			}
       footerMessage={message ?? ""}
		/>
	);
}
// #endregion